import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    ShieldCheckIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    FolderOpenIcon,
    UsersIcon,
    ChartPieIcon
} from '@heroicons/react/24/outline';

// --- START: MOCK DATA for PF & ESI Report ---
const mockPFESIData = [
    {
        id: 1,
        name: 'Aisha Sharma',
        employeeId: 'EMP001',
        department: 'Engineering',
        employeePF: 1800,
        employerPF: 1800,
        employeeESI: 500,
        employerESI: 1350,
    },
    {
        id: 2,
        name: 'Rahul Verma',
        employeeId: 'EMP002',
        department: 'Human Resources',
        employeePF: 1500,
        employerPF: 1500,
        employeeESI: 400,
        employerESI: 1100,
    },
    {
        id: 3,
        name: 'Priya Singh',
        employeeId: 'EMP003',
        department: 'Marketing',
        employeePF: 1650,
        employerPF: 1650,
        employeeESI: 450,
        employerESI: 1250,
    },
    {
        id: 4,
        name: 'Amit Patel',
        employeeId: 'EMP004',
        department: 'Sales',
        employeePF: 1900,
        employerPF: 1900,
        employeeESI: 550,
        employerESI: 1500,
    },
    {
        id: 5,
        name: 'Neha Gupta',
        employeeId: 'EMP005',
        department: 'Engineering',
        employeePF: 1750,
        employerPF: 1750,
        employeeESI: 480,
        employerESI: 1300,
    },
];
// --- END: MOCK DATA ---

const PFESIReport = () => {
    const [filterDept, setFilterDept] = useState('All');

    // Calculate summary statistics
    const totalEmployeePF = mockPFESIData.reduce((sum, item) => sum + item.employeePF, 0);
    const totalEmployerPF = mockPFESIData.reduce((sum, item) => sum + item.employerPF, 0);
    const totalEmployeeESI = mockPFESIData.reduce((sum, item) => sum + item.employeeESI, 0);
    const totalEmployerESI = mockPFESIData.reduce((sum, item) => sum + item.employerESI, 0);

    const filteredData = mockPFESIData.filter(item => {
        return filterDept === 'All' || item.department === filterDept;
    });

    const uniqueDepartments = ['All', ...new Set(mockPFESIData.map(item => item.department))];

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    };

    const cardVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.5, delay: 0.2 } },
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-100 to-sky-200 p-8 text-gray-800">
            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-7xl mx-auto"
            >
                <h1 className="text-4xl font-extrabold text-indigo-800 flex items-center mb-4">
                    <ShieldCheckIcon className="h-10 w-10 text-indigo-600 mr-4" />
                    PF & ESI Report
                </h1>
                <p className="text-xl font-light text-indigo-600 mb-8">
                    Detailed breakdown of social security contributions.
                </p>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <motion.div
                        variants={cardVariants}
                        className="bg-white p-6 rounded-3xl shadow-lg flex flex-col items-center justify-center space-y-2"
                    >
                        <div className="p-3 bg-blue-100 rounded-full">
                            <ArrowDownIcon className="h-8 w-8 text-blue-600" />
                        </div>
                        <p className="text-gray-500 font-semibold text-center">Total Employee PF</p>
                        <h2 className="text-2xl font-bold text-gray-900">
                            ₹{totalEmployeePF.toLocaleString('en-IN')}
                        </h2>
                    </motion.div>
                    <motion.div
                        variants={cardVariants}
                        className="bg-white p-6 rounded-3xl shadow-lg flex flex-col items-center justify-center space-y-2"
                    >
                        <div className="p-3 bg-red-100 rounded-full">
                            <ArrowUpIcon className="h-8 w-8 text-red-600" />
                        </div>
                        <p className="text-gray-500 font-semibold text-center">Total Employer PF</p>
                        <h2 className="text-2xl font-bold text-gray-900">
                            ₹{totalEmployerPF.toLocaleString('en-IN')}
                        </h2>
                    </motion.div>
                    <motion.div
                        variants={cardVariants}
                        className="bg-white p-6 rounded-3xl shadow-lg flex flex-col items-center justify-center space-y-2"
                    >
                        <div className="p-3 bg-green-100 rounded-full">
                            <ArrowDownIcon className="h-8 w-8 text-green-600" />
                        </div>
                        <p className="text-gray-500 font-semibold text-center">Total Employee ESI</p>
                        <h2 className="text-2xl font-bold text-gray-900">
                            ₹{totalEmployeeESI.toLocaleString('en-IN')}
                        </h2>
                    </motion.div>
                    <motion.div
                        variants={cardVariants}
                        className="bg-white p-6 rounded-3xl shadow-lg flex flex-col items-center justify-center space-y-2"
                    >
                        <div className="p-3 bg-yellow-100 rounded-full">
                            <ArrowUpIcon className="h-8 w-8 text-yellow-600" />
                        </div>
                        <p className="text-gray-500 font-semibold text-center">Total Employer ESI</p>
                        <h2 className="text-2xl font-bold text-gray-900">
                            ₹{totalEmployerESI.toLocaleString('en-IN')}
                        </h2>
                    </motion.div>
                </div>

                {/* Filter & Search Section */}
                <div className="bg-white p-6 rounded-3xl shadow-lg mb-8">
                    <h3 className="text-xl font-bold mb-4 flex items-center text-gray-900">
                        <FolderOpenIcon className="h-6 w-6 mr-2 text-gray-500" />
                        Filter Report
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                                <UsersIcon className="h-5 w-5" />
                            </div>
                            <select
                                value={filterDept}
                                onChange={(e) => setFilterDept(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white appearance-none"
                            >
                                {uniqueDepartments.map((dept, index) => (
                                    <option key={index} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </div>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                                <ChartPieIcon className="h-5 w-5" />
                            </div>
                            <input
                                type="text"
                                placeholder="Select Period (Mock)"
                                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                disabled
                            />
                        </div>
                    </div>
                </div>

                {/* Report Table */}
                <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee PF</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employer PF</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee ESI</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employer ESI</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Contribution</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredData.length > 0 ? (
                                    filteredData.map(item => (
                                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.employeeId}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.name}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.department}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">₹{item.employeePF.toLocaleString('en-IN')}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">₹{item.employerPF.toLocaleString('en-IN')}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">₹{item.employeeESI.toLocaleString('en-IN')}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">₹{item.employerESI.toLocaleString('en-IN')}</td>
                                            <td className="px-6 py-4 whitespace-now-wrap text-sm font-bold text-gray-900">
                                                ₹{(item.employeePF + item.employerPF + item.employeeESI + item.employerESI).toLocaleString('en-IN')}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="px-6 py-4 text-center text-gray-500">No results found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default PFESIReport;